import { ReactNode } from 'react'
import styled, { css } from 'styled-components'
import { toast, Toast as HotToast } from 'react-hot-toast/headless'

export type Appearance = 'success' | 'error' | 'info' | 'warning' | 'alert'

type WrapperProps = {
  $appearance: Appearance
  $visible: boolean
}

const appearanceStyles = ({
  $appearance,
  theme,
}: WrapperProps & { theme: any }) => {
  if ($appearance === 'error') {
    return css`
      color: white;
      background-color: ${theme.colors.errorRed};
    `
  }
  if ($appearance === 'warning' || $appearance === 'alert') {
    return css`
      color: #333;
      background-color: ${theme.brand.sunup};
    `
  }
  return css`
    color: white;
    background-color: ${theme.colors.success};
  `
}

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 2px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  font-size: 14px;
  line-height: 1.4;
  max-width: 360px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible }) =>
    $visible ? 'translateY(0)' : 'translateY(8px)'};
  transition: opacity 200ms ease, transform 200ms ease;

  a {
    color: inherit;
    text-decoration: underline;
  }

  ${appearanceStyles}
`

const Content = styled.div`
  flex: 1;
  min-width: 0;
`

const DismissButton = styled.button`
  flex: 0 0 auto;
  background: transparent;
  border: 0;
  padding: 0 4px;
  color: inherit;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`

type Props = {
  t: HotToast
  appearance: Appearance
  children: ReactNode
}

const ToastBody = ({ t, appearance, children }: Props) => (
  <Wrapper $appearance={appearance} $visible={t.visible}>
    <Content>
      {typeof children === 'string' ? (
        // Server flash alerts and many call sites pass HTML strings (e.g. with
        // <a> tags); preserve the existing dangerouslySetInnerHTML behaviour.
        // eslint-disable-next-line react/no-danger
        <div dangerouslySetInnerHTML={{ __html: children }} />
      ) : (
        children
      )}
    </Content>
    <DismissButton
      type="button"
      aria-label="Dismiss notification"
      onClick={() => toast.dismiss(t.id)}
    >
      ×
    </DismissButton>
  </Wrapper>
)

export default ToastBody
